import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Branch } from '@/queries/useBranches'
import { Edit, Search, Settings, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface BranchListProps {
  branches: Branch[] | undefined
  isLoading: boolean
  error: Error | null
  onEdit: (branch: Branch) => void
  onDelete: (branch: Branch) => void
  onSettings?: (branch: Branch) => void
}

export default function BranchList({
  branches,
  isLoading,
  error,
  onEdit,
  onDelete,
  onSettings,
}: BranchListProps) {
  const { t } = useTranslation('admin')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

  // Debounce search query (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // Client-side filtering by name, address (using debounced query)
  const filteredBranches = useMemo(() => {
    if (!branches) return []
    if (!debouncedSearchQuery.trim()) return branches

    const query = debouncedSearchQuery.toLowerCase().trim()
    return branches.filter(
      (branch) =>
        branch.name.toLowerCase().includes(query) ||
        branch.address?.toLowerCase().includes(query)
    )
  }, [branches, debouncedSearchQuery])

  if (error) {
    return (
      <Alert variant="destructive" data-testid="branch-list-error">
        <AlertDescription>
          {t('branches.error.loadFailed')}
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <div 
      className="flex flex-col gap-[var(--spacing-md)]"
      data-testid="branch-list"
    >
      {/* Search Bar */}
      <div className="relative" data-testid="branch-list-search">
        <Search
          className="absolute left-[var(--space-4)] top-1/2 -translate-y-1/2 pointer-events-none z-10 w-4 h-4 text-[var(--text-secondary)] rtl:left-auto rtl:right-[var(--space-4)]"
        />
        <Input
          type="text"
          placeholder={t('branches.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-[var(--space-10)] rtl:pl-0 rtl:pr-[var(--space-10)]"
          data-testid="branch-list-search-input"
        />
      </div>

      {/* Results Count */}
      {debouncedSearchQuery && (
        <p
          className="text-[var(--text-sm)] text-[var(--text-secondary)]"
          data-testid="branch-list-results-count"
        >
          {t('branches.resultsCount', { count: filteredBranches.length })}
        </p>
      )}

      {/* Loading State */}
      {isLoading && (
        <div 
          className="flex flex-col gap-[var(--spacing-sm)]"
          data-testid="branch-list-loading"
        >
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredBranches.length === 0 && (
        <div
          className="text-center pt-[var(--spacing-2xl)] pb-[var(--spacing-2xl)] text-[var(--text-secondary)]"
          data-testid="branch-list-empty"
        >
          <p 
            className="text-[var(--text-lg)] font-medium mb-[var(--spacing-sm)]"
          >
            {t('branches.emptyState.title')}
          </p>
          <p className="text-[var(--text-sm)]">
            {debouncedSearchQuery
              ? t('branches.emptyState.searchMessage')
              : t('branches.emptyState.noDataMessage')}
          </p>
        </div>
      )}

      {/* Branch Table */}
      {!isLoading && filteredBranches.length > 0 && (
        <Table data-testid="branch-list-table">
          <TableHeader>
            <TableRow>
              <TableHead data-testid="branch-list-header-name">{t('branches.table.branch')}</TableHead>
              <TableHead data-testid="branch-list-header-address">{t('branches.table.address')}</TableHead>
              <TableHead className="text-left rtl:text-right" data-testid="branch-list-header-actions">
                {t('branches.table.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredBranches.map((branch) => (
              <TableRow key={branch.id} data-testid={`branch-row-${branch.id}`}>
                <TableCell
                  className="font-medium"
                  data-testid={`branch-name-${branch.id}`}
                >
                  {branch.name}
                </TableCell>
                <TableCell data-testid={`branch-address-${branch.id}`}>
                  {branch.address || '-'}
                </TableCell>
                <TableCell className="text-left rtl:text-right">
                  <div className="flex justify-start gap-[var(--spacing-sm)] rtl:justify-end">
                    {onSettings && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onSettings(branch)}
                        data-testid={`branch-settings-button-${branch.id}`}
                      >
                        <Settings className="h-4 w-4 mr-[var(--spacing-xs)] rtl:mr-0 rtl:ml-[var(--spacing-xs)]" />
                        {t('branches.settings')}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(branch)}
                      data-testid={`branch-edit-button-${branch.id}`}
                    >
                      <Edit className="h-4 w-4 mr-[var(--spacing-xs)] rtl:mr-0 rtl:ml-[var(--spacing-xs)]" />
                      {t('common:buttons.edit')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(branch)}
                      data-testid={`branch-delete-button-${branch.id}`}
                    >
                      <Trash2 className="h-4 w-4 mr-[var(--spacing-xs)] rtl:mr-0 rtl:ml-[var(--spacing-xs)]" />
                      {t('common:buttons.delete')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}

